import { Region, TableProps } from '@blueprintjs/table';
import {
  IColumnIndices,
  IRowIndices,
} from '@blueprintjs/table/lib/esm/common/grid';
import * as Monaco from 'monaco-editor/esm/vs/editor/editor.api';
import uuid from 'uuid/v4';
import { VoiceboxMessage } from 'vet-bones/constants/voicebox';

import { FileSystemFileHandle } from 'src/browser/constants/FileSystemAccess';
import { LanguageId } from 'src/common/constants/LanguageId';
import { QueryType } from 'src/common/constants/QueryType';
import { DEFAULT_REASONING_SCHEMA } from 'src/common/store/databases/DatabasesState';
import { RawJsonLdData } from 'src/common/store/visualization/VisualizationState';
import { Binding } from 'src/common/utils/rpc/types';

export function isBindingsResult(body: any): body is BindingsResult {
  return body.results && body.results.bindings && body.head && body.head.vars;
}

export interface BindingsResult {
  results: {
    bindings: Array<{
      [column: string]: Binding;
    }>;
  };
  head: {
    vars: string[];
  };
}

export function hasJsonLdResults(jsonLdBody?: JsonLdBody) {
  return (
    jsonLdBody &&
    jsonLdBody.queryHistoryId &&
    Array.isArray(jsonLdBody.results) &&
    jsonLdBody.results.length
  );
}

export interface JsonLdBody {
  queryHistoryId: string;
  results?: RawJsonLdData[];
}

export type ScrollPosition = [
  IRowIndices['rowIndexStart'],
  IRowIndices['rowIndexEnd'],
  IColumnIndices['columnIndexStart'],
  IColumnIndices['columnIndexEnd']
];

export const enum NoteStorageType {
  FILESYSTEM = 'FILESYSTEM',
  NONE = 'NONE',
  RESERVED = 'RESERVED',
  STARDOG = 'STARDOG',
}

export interface EditorSettings {
  activeDatabase: string;
  changedWhileInactive: boolean;
  languageId: LanguageId;
  namedGraphs: string[];
  reasoning: boolean;
  timeout: string;
  schema: string;
  query: string;
  latestSavedVersionId: number;
  handle?: FileSystemFileHandle;
  // indicates whether a note's current query content has been saved
  isSaved: boolean;
  viewState?: Monaco.editor.ICodeEditorViewState;
  height?: number;
  heightPercent?: number;
  heightPercentMaxHeight?: number;
  isStoreQueryPopoverOpen: boolean;
  isQueryConfigPopoverOpen: boolean;
  isSchemaDialogOpen: boolean;
  // visualization settings id for accessing visualization state
  visualizationSettingsId: string;
}

export const enum ResultViewType {
  TEXTUAL = 'textual',
  VISUAL = 'visual',
  SANDDANCE = 'sanddance',
}

export interface ResultSettings {
  contentType?: string;
  body?: string | Record<string, any> | BindingsResult | boolean;
  jsonLdBody?: JsonLdBody;
  query?: string;
  queryId?: string;
  languageId?: LanguageId;
  reasoning?: boolean;
  database?: string;
  pending: boolean;
  pendingSandDance: boolean;
  resultsProcessed?: boolean;
  columnWidths?: TableProps['columnWidths'];
  scrollPosition?: ScrollPosition;
  selection?: Region[];
  viewType: ResultViewType;
  isCollapsed: boolean;
  typeOfQuery?: QueryType;
  height?: number;
  heightPercent?: number;
  heightPercentMaxHeight?: number;
  timeElapsed?: number;
  pathStart?: string; // Name of binding for the start of a path result
  pathEnd?: string; // Name of binding for the end of a path result
}

export interface VoiceboxSettings {
  connectionIdx?: number;
  conversationId?: string;
  /** whether the voicebox drawer is open */
  isOpen?: boolean;
  /** current messages sent to voicebox to get sparql query */
  messages?: VoiceboxMessage[];
  /** voicebox request is running */
  pending?: boolean;
}

// Used when instantiating a new saved tab
export const defaultEditorSettings: EditorSettings = {
  activeDatabase: '',
  namedGraphs: [],
  changedWhileInactive: false,
  languageId: null, // this should be set based on the new note's content and context
  reasoning: false,
  timeout: '',
  schema: DEFAULT_REASONING_SCHEMA,
  query: '',
  latestSavedVersionId: 1, // monaco's version ids are 1-indexed
  isSaved: true,
  isStoreQueryPopoverOpen: false,
  isQueryConfigPopoverOpen: false,
  isSchemaDialogOpen: false,
  visualizationSettingsId: '',
};

export const defaultResultSettings: ResultSettings = {
  viewType: ResultViewType.TEXTUAL,
  isCollapsed: true,
  pending: false,
  pendingSandDance: false,
};

export const defaultVoiceboxSettings: VoiceboxSettings = {
  conversationId: undefined,
  isOpen: undefined,
  pending: undefined,
};

export class NoteState {
  public isPreview = false;

  public editorSettings = {
    ...defaultEditorSettings,
    visualizationSettingsId: uuid(),
  };

  public resultSettings = {
    ...defaultResultSettings,
  };

  public totalQueries = 0;

  public pane = 0;

  public isUntitled = true;

  // either the file or stored query that the note is tied to
  public target: string | null = null;

  public voiceboxSettings = {
    ...defaultVoiceboxSettings,
  };

  // Sadly, having a setter for id that returns an id based on storageType & name
  // does not play nicely with how we rehydrate the store, and does not persist.
  constructor(
    public storageType: NoteStorageType,
    public name: string,
    public id: string
  ) {}
}
